/**
 * SMS retention panel — operations view of the nightly SMS retention job (owner request 2026-10-02).
 *
 * Retention deletes the text of SMS messages older than their retention period. It runs every
 * 5 minutes between 03:00 and 04:55 UTC. A watchdog (migration 2026-10-02-040) turns it OFF if a run
 * holds locks longer than the 5-second tripwire, a run never finishes, or the cron execution fails,
 * and alerts the superadmins and the named owner. This panel answers "is retention on, and if not,
 * why?" and shows the numbers behind it.
 *
 * Read-only by design: there is no button to turn retention back on. Re-enabling is a deliberate
 * decision after investigating, done in SQL (the line is shown below for admins).
 *
 * Authorization is server-side (sms_retention_health() refuses anyone but an active admin or
 * superadmin); the routes that render this panel mirror that.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import LoadingSpinner from "../common/LoadingSpinner";
import { isFeatureEnabled } from "../../utils/featureFlags";
import {
  getSmsRetentionHealth,
  type SmsRetentionHealth,
} from "../../services/smsRetentionHealth.service";

const REFRESH_MS = 60_000;
const DAY_MS = 24 * 60 * 60 * 1000;
const REENABLE_SQL =
  "SELECT cron.alter_job(jobid, active := true) FROM cron.job WHERE jobname = 'sms-retention';";

function when(ts: string): string {
  const d = new Date(ts);
  return `${format(d, "MMM d, HH:mm")} (${formatDistanceToNow(d, { addSuffix: true })})`;
}

type Status =
  | { kind: "not_installed" }
  | { kind: "on" }
  | { kind: "auto_off"; at: string; reason: string }
  | { kind: "manual_off" };

function statusOf(h: SmsRetentionHealth): Status {
  if (!h.retention.exists) {
    return { kind: "not_installed" };
  }
  if (h.retention.active) {
    return { kind: "on" };
  }
  const trip = h.trips.find((t) => t.cron_was_active);
  return trip
    ? { kind: "auto_off", at: trip.tripped_at, reason: trip.reason }
    : { kind: "manual_off" };
}

const SmsRetentionPanel: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["sms-retention-health"],
    queryFn: getSmsRetentionHealth,
    refetchInterval: REFRESH_MS,
  });

  if (isLoading) {
    return (
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        SMS retention status could not be loaded:{" "}
        {error instanceof Error ? error.message : "unknown error"}
      </div>
    );
  }

  const status = statusOf(data);
  const since = Date.now() - DAY_MS;
  const recent = data.runs.filter(
    (r) => new Date(r.started_at).getTime() >= since,
  );
  const locks = recent
    .map((r) => r.locks_held_ms)
    .filter((n): n is number => typeof n === "number");
  const maxLock = locks.length ? Math.max(...locks) : null;
  const unfinished = recent.filter((r) => !r.finished_at).length;
  const failed = data.failed_executions.filter(
    (f) => new Date(f.start_time).getTime() >= since,
  ).length;
  const overTripwire = maxLock !== null && maxLock > data.tripwire_ms;

  const badge =
    status.kind === "on"
      ? "bg-green-100 text-green-800"
      : status.kind === "not_installed"
        ? "bg-gray-100 text-gray-700"
        : "bg-red-100 text-red-800";
  const badgeText =
    status.kind === "on"
      ? "On"
      : status.kind === "not_installed"
        ? "Not installed"
        : "Off";

  return (
    <div
      className="mb-6 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="sms-retention-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-700">
            SMS retention
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Deletes the text of old SMS messages, every 5 minutes from 03:00 to
            04:55 UTC. A watchdog turns it off automatically if a run holds
            locks over {(data.tripwire_ms / 1000).toFixed(0)} seconds, never
            finishes, or fails.
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${badge}`}
        >
          {badgeText}
        </span>
      </div>

      <div className="mt-3 text-sm text-gray-800">
        {status.kind === "on" && <p>Retention is on.</p>}
        {status.kind === "not_installed" && (
          <p>Retention is not installed in this environment yet.</p>
        )}
        {status.kind === "auto_off" && (
          <p>
            Off: stopped automatically at {when(status.at)}: {status.reason}.
          </p>
        )}
        {status.kind === "manual_off" && <p>Off: turned off by hand.</p>}
        {!data.watchdog.active && status.kind !== "not_installed" && (
          <p className="mt-1 text-amber-700">
            The watchdog itself is{" "}
            {data.watchdog.exists ? "turned off" : "not installed"}, so nothing
            will stop retention automatically.
          </p>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded border border-gray-100 p-3">
          <div className="text-xs text-gray-500">Runs, last 24 hours</div>
          <div className="text-lg font-semibold text-gray-900">
            {recent.length}
          </div>
        </div>
        <div className="rounded border border-gray-100 p-3">
          <div className="text-xs text-gray-500">
            Longest lock hold (tripwire {(data.tripwire_ms / 1000).toFixed(0)}{" "}
            s)
          </div>
          <div
            className={`text-lg font-semibold ${overTripwire ? "text-red-700" : "text-gray-900"}`}
          >
            {maxLock === null ? "—" : `${Math.round(maxLock)} ms`}
          </div>
        </div>
        <div className="rounded border border-gray-100 p-3">
          <div className="text-xs text-gray-500">Failures, last 24 hours</div>
          <div
            className={`text-lg font-semibold ${failed + unfinished > 0 ? "text-red-700" : "text-gray-900"}`}
          >
            {failed + unfinished}
          </div>
          {(failed > 0 || unfinished > 0) && (
            <div className="text-xs text-gray-500">
              {failed} failed cron run{failed === 1 ? "" : "s"}, {unfinished}{" "}
              unfinished
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Automatic stops
        </div>
        {data.trips.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500">None so far.</p>
        ) : (
          <ul className="mt-1 space-y-1 text-sm text-gray-800">
            {data.trips.map((t) => (
              <li key={t.id}>
                {when(t.tripped_at)}: {t.reason}
                {t.cron_was_active === false
                  ? " (retention was already off)"
                  : ""}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-800">
        <span className="font-medium">Held past retention: </span>
        {data.held_past_24_months === null
          ? "no completed run yet"
          : data.held_past_24_months}
        {isFeatureEnabled("operatorSmsConversations") ? (
          <>
            {" · "}
            <Link
              to="/reports/sms-retention-held"
              className="text-primary-600 hover:underline"
            >
              Open the report
            </Link>
          </>
        ) : (
          <span className="text-gray-500">
            {" "}
            (the report page is not switched on yet; run
            sms_retention_held_report() as an admin in SQL)
          </span>
        )}
      </div>

      {status.kind !== "on" && status.kind !== "not_installed" && (
        <div className="mt-4 rounded bg-gray-50 p-3 text-xs text-gray-700">
          Turning retention back on is a deliberate decision after investigating
          the cause. Run in SQL:{" "}
          <code className="break-all font-mono">{REENABLE_SQL}</code>
        </div>
      )}
    </div>
  );
};

export default SmsRetentionPanel;
